<script setup lang="ts">
import {
  WdsBadge,
  WdsButtonGhost,
  WdsCheckbox,
  WdsIcon,
  WdsStack,
  WdsText,
} from '@worksome/wds-components'
import { computed, ComputedRef, ref, useTemplateRef, watch } from 'vue'
import { useElementBounding } from '@vueuse/core'
import {
  ExecutableDefinitionNode,
  FieldNode,
  Kind,
  SelectionNode,
  SelectionSetNode,
} from 'graphql/index'
import { $t } from '@/app-bootstrapping/SetUpInternationalization'
import ButtonDropdown from '@/components/ButtonDropdown.vue'
import {
  useColumnOrdering,
  useQueryColumns,
  useQueryOperationDefinition,
  useRootType,
} from '@/components/GraphqlDataTable/composables'
import {
  ColumnFragmentListSupport,
  type GraphqlColumnMap,
  type ListSupportExpand,
} from '@/components/GraphqlDataTable/columns/types'
import { FieldTypeFragment } from '@/gql/graphql'
import { useFragment } from '@/gql'
import { FieldTypeFragmentDef, TypeRefFragmentDef } from '@/components/GraphqlDataTable/queries'
import ColumnMap from '@/components/GraphqlDataTable/columns'
import {
  containsListKind,
  encodeExpandedColumnKey,
  fieldToReadAble,
  getFieldTypeLeaf,
} from '@/components/GraphqlDataTable/tools'
import { zip } from '@/utils/tools'

const props = withDefaults(
  defineProps<{
    query: string
    allowCrossTableColumns: boolean
    selectableColumns?: 'query' | 'dynamic' | 'all' | 'none'
    /** Fragment / field keys excluded from the table (matches GraphqlDataTable columnBlackList). */
    columnBlackList?: string[]
  }>(),
  {
    selectableColumns: 'query',
  },
)

const columnBlackListSet = computed(() => new Set(props.columnBlackList ?? []))

const isColumnBlacklisted = (listKey: string, option: GraphqlColumnMap) =>
  columnBlackListSet.value.has(listKey) || columnBlackListSet.value.has(option.columnDef.key)

const { dataType } = useRootType(props.query)
const { fieldToColumn } = useQueryColumns(props.query)
const {
  columnsWhiteList,
  expandedColumnTitles,
  columnOrdering,
  defaultColumnSelection,
  defaultColumnOrdering,
  resetColumnSelection,
  dragEventHandler,
} = useColumnOrdering(props.query)
const { queryOperationDefinition } = useQueryOperationDefinition(props.query)

const draggingKey = ref<string | null>(null)

// The dropdown opens wherever the trigger sits, so the list gets whatever viewport is left below it.
const columnList = useTemplateRef<HTMLElement>('columnList')
const { top: columnListTop, update: updateColumnListBounds } = useElementBounding(columnList)
const columnListMaxHeight = computed(() => `calc(100vh - ${Math.max(columnListTop.value, 0)}px - 1rem)`)

const onDragStart = (key: string, columnDef: any) => {
  draggingKey.value = key
  dragEventHandler('dragstart', columnDef, true)
}

const onDragEnd = (columnDef: any) => {
  draggingKey.value = null
  dragEventHandler('dragend', columnDef, true)
}

const findDataField = (field: SelectionNode | ExecutableDefinitionNode) => {
  if (field.kind === Kind.INLINE_FRAGMENT) return null
  if (field.name?.value === 'data') return field
  if (field.kind === Kind.FRAGMENT_SPREAD || !field.selectionSet) return null
  for (const fieldElement of field.selectionSet.selections) {
    const res = findDataField(fieldElement)
    if (res !== null) return res
  }
  return null
}
const complexFieldTypes: ComputedRef<{ path: string; type: string | null | undefined }[]> =
  computed(() => {
    if (!dataType.value) return []

    return (
      dataType.value.fields
        ?.map((field) => useFragment(FieldTypeFragmentDef, field))
        .map((field) => ({
          f: field,
          kind: getFieldTypeLeaf(useFragment(TypeRefFragmentDef, field.type)).kind,
        }))
        .filter(({ f }) => f.args.length === 0)
        .filter(({ kind }) => kind === 'OBJECT')
        .filter(({ f: { type } }) => !containsListKind(useFragment(TypeRefFragmentDef, type)))
        .map(({ f }) => ({
          path: f.name,
          type: getFieldTypeLeaf(useFragment(TypeRefFragmentDef, f.type)).name,
        })) ?? []
    )
  })

const compatibleColumns = computed(() => {
  if (!queryOperationDefinition.value || !dataType.value) return []
  const dataSelections =
    findDataField(queryOperationDefinition.value)?.selectionSet?.selections ?? []

  // Find all compatible types of columns
  const dtype = dataType.value.name
  const validTypes = props.allowCrossTableColumns
    ? [...complexFieldTypes.value.map(({ type }) => type), dtype]
    : [dtype]

  const orderIndex = new Map(columnOrdering.value.map((k, i) => [k, i] as [string, number]))

  const fieldTypes: [string, GraphqlColumnMap][] = dataSelections
    .filter(
      (field: SelectionSetNode | FieldNode) => field.kind === Kind.FIELD && !field?.selectionSet,
    )
    .map((field: FieldNode) => {
      const fieldType: FieldTypeFragment | undefined = dataType.value?.fields
        ?.map((f) => useFragment(FieldTypeFragmentDef, f))
        .find((ft) => ft.name === field.name.value)
      return [field.name.value, fieldToColumn(fieldType)]
    })
    .filter(([_, col]) => col !== null)

  return [
    ...fieldTypes,
    ...Object.keys(ColumnMap)
      .map((key): [string, GraphqlColumnMap] => [key, ColumnMap[key]])
      .filter(([key, value]) => {
        // Exclude LIST-support columns from standard list — they're shown as expandable items
        if (value.listSupport?.supportType === ColumnFragmentListSupport.LIST) return false
        const { fragment } = value
        if (defaultColumnSelection.value[key]) {
          return true
        }
        if (!props.allowCrossTableColumns && !defaultColumnSelection.value[key]) {
          return false
        }
        if (fragment !== undefined && 'typeCondition' in fragment.definitions[0]) {
          return validTypes.includes(fragment.definitions[0]?.typeCondition.name.value)
        }
        return false
      }),
  ]
    .filter(([key, option]) => !isColumnBlacklisted(key, option))
    .sort(([_, a], [__, b]) => {
      if (columnOrdering.value.length === 0) return 0
      const ai = orderIndex.has(a.columnDef.key) ? (orderIndex.get(a.columnDef.key) ?? 0) : Infinity
      const bi = orderIndex.has(b.columnDef.key) ? (orderIndex.get(b.columnDef.key) ?? 0) : Infinity
      return ai - bi
    })
})

const modifiedTableCount = computed(() => {
  if (Object.keys(defaultColumnSelection.value).length === 0) return 0
  const whiteList = Object.keys(columnsWhiteList.value)
    .filter((key) => !columnBlackListSet.value.has(key))
    .map(
      (key) =>
        (columnsWhiteList.value[key] && defaultColumnSelection.value[key]) ||
        (!columnsWhiteList.value[key] && !defaultColumnSelection.value[key]),
    )
  return whiteList.reduce((a, b) => a + Number(!b), 0)
})

const hasCustomOrdering = computed(
  () => !zip(columnOrdering.value, defaultColumnOrdering.value).every(([c, d]) => c === d),
)

const hasModifiedColumns = computed(() => hasCustomOrdering.value || modifiedTableCount.value > 0)

// Find all columns with LIST support that are compatible with the root type
const expandableColumns = computed(() => {
  if (!dataType.value) return []
  const rootTypeName = dataType.value.name
  return Object.entries(ColumnMap)
    .filter(([_, col]) => col.listSupport?.supportType === ColumnFragmentListSupport.LIST)
    .filter(([key, col]) => !isColumnBlacklisted(key, col))
    .filter(([_, col]) => {
      const fragDef = col.fragment?.definitions[0]
      if (!fragDef || fragDef.kind !== Kind.FRAGMENT_DEFINITION) return false
      const targetType = fragDef.typeCondition.name.value

      // Fragment on the root type itself (e.g. fragment on Worker with a compliances list field)
      if (targetType === rootTypeName) return true

      // Fragment on a nested list item type (e.g. fragment on CustomFieldValue)
      return dataType.value?.fields
        ?.map((f) => useFragment(FieldTypeFragmentDef, f))
        .some((f) => {
          const leaf = getFieldTypeLeaf(useFragment(TypeRefFragmentDef, f.type))
          return (
            leaf.name === targetType && containsListKind(useFragment(TypeRefFragmentDef, f.type))
          )
        })
    })
})

// Eagerly call useAvailableItems for all LIST columns at setup time
// (composables must run during setup, not inside computed/watch).
// TODO: defer until the column manager dropdown is first opened — with the custom-fields
// branch this becomes more expensive because each dynamic custom field loader hits the API.
const allExpandableItemSources = Object.entries(ColumnMap)
  .filter(([_, col]) => col.listSupport?.supportType === ColumnFragmentListSupport.LIST)
  .map(([name, col]) => ({
    name,
    column: col,
    ...(col.listSupport as ListSupportExpand).useAvailableItems(),
  }))

// Reactively filter to only those compatible with the root type
const expandableItemSources = computed(() => {
  const compatibleNames = new Set(expandableColumns.value.map(([name]) => name))
  return allExpandableItemSources.filter(({ name }) => compatibleNames.has(name))
})

// Write titles when items load
watch(
  () => allExpandableItemSources.map((s) => s.items.value),
  () => {
    for (const { name, items } of allExpandableItemSources) {
      for (const item of items.value) {
        const key = encodeExpandedColumnKey(name, item.key)
        expandedColumnTitles.value[key] = item.title
      }
    }
  },
  { immediate: true, deep: true },
)

const toggleExpandedColumn = (baseName: string, item: { key: string; title: string }) => {
  const key = encodeExpandedColumnKey(baseName, item.key)
  expandedColumnTitles.value[key] = item.title
  const isEnabling = !columnsWhiteList.value[key]
  columnsWhiteList.value[key] = isEnabling
  if (isEnabling) {
    if (!columnOrdering.value.includes(key)) {
      columnOrdering.value = [...columnOrdering.value, key]
    }
  } else {
    columnOrdering.value = columnOrdering.value.filter((k) => k !== key)
  }
}

const customizeColumnsTitle = (column: GraphqlColumnMap | string) => {
  if (typeof column === 'string') return fieldToReadAble(column)
  if (column.columnDef.title) return column.columnDef.title

  return fieldToReadAble(column.columnDef.key)
}
</script>

<template>
  <ButtonDropdown no-caret ghost-button data-test="sort-button">
    <WdsStack gap-sm align-center justify-center>
      <WdsBadge
        v-if="hasModifiedColumns"
        float
        primary
        class="tw-flex tw-items-center tw-justify-center"
      >
        <template #badge-subject>
          <WdsIcon name="settings" :size="18" weight="thin" />
        </template>
        <span>&nbsp;</span>
      </WdsBadge>
      <WdsIcon v-else name="settings" :size="18" weight="thin" />
    </WdsStack>

    <template #dropdown>
      <WdsStack direction-column gap-md class="tw-p-5" @pointerenter="updateColumnListBounds">
        <WdsStack direction-column gap-md class="tw-w-64">
          <WdsStack direction-row align-center justify-space-between>
            <WdsText secondary :text="$t('Customize table')" />
            <WdsButtonGhost secondary @click="resetColumnSelection">
              {{ $t('Reset') }}
            </WdsButtonGhost>
          </WdsStack>
        </WdsStack>
        <WdsStack
          ref="columnList"
          direction-column
          gap-md
          class="tw-w-64 tw-overflow-auto tw-overscroll-contain"
          :style="{ maxHeight: columnListMaxHeight }"
          @dragover.prevent
          @drop.prevent
        >
          <div
            v-for="[key, option] in compatibleColumns"
            :key="key"
            draggable="true"
            :class="{ 'column-dragging': draggingKey === key }"
            @dragover.prevent="dragEventHandler('dragover', option.columnDef, true)"
            @dragstart="onDragStart(key, option.columnDef)"
            @dragend="onDragEnd(option.columnDef)"
            @drop.prevent
          >
            <WdsStack direction-row items-center class="tw-w-full" gap-sm>
              <div style="cursor: move">
                <WdsIcon :size="16" color="#B2B2BF" name="draggable" />
              </div>
              <WdsCheckbox v-model:checked="columnsWhiteList[key]" class="tw-flex">
                <WdsStack direction-row justify-space-between items-center gap-sm>
                  {{ customizeColumnsTitle(option) }}
                </WdsStack>
              </WdsCheckbox>
            </WdsStack>
          </div>

          <template v-for="source in expandableItemSources" :key="source.name">
            <WdsStack v-if="source.items.value.length > 0" direction-column gap-sm>
              <WdsText secondary class="tw-text-xs tw-mt-2">
                {{ source.column.columnDef.title }}
              </WdsText>
              <div
                v-for="item in source.items.value"
                :key="item.key"
                draggable="true"
                :class="{
                  'column-dragging': draggingKey === encodeExpandedColumnKey(source.name, item.key),
                }"
                @dragover.prevent="
                  dragEventHandler(
                    'dragover',
                    {
                      key: encodeExpandedColumnKey(source.name, item.key),
                      title: item.title,
                      draggable: true,
                    },
                    true,
                  )
                "
                @dragstart="
                  onDragStart(encodeExpandedColumnKey(source.name, item.key), {
                    key: encodeExpandedColumnKey(source.name, item.key),
                    title: item.title,
                    draggable: true,
                  })
                "
                @dragend="
                  onDragEnd({
                    key: encodeExpandedColumnKey(source.name, item.key),
                    title: item.title,
                    draggable: true,
                  })
                "
                @drop.prevent
              >
                <WdsStack direction-row items-center class="tw-w-full" gap-sm>
                  <div style="cursor: move">
                    <WdsIcon :size="16" color="#B2B2BF" name="draggable" />
                  </div>
                  <WdsCheckbox
                    :checked="!!columnsWhiteList[encodeExpandedColumnKey(source.name, item.key)]"
                    class="tw-flex"
                    @update:checked="toggleExpandedColumn(source.name, item)"
                  >
                    <WdsStack direction-row justify-space-between items-center gap-sm>
                      {{ item.title }}
                    </WdsStack>
                  </WdsCheckbox>
                </WdsStack>
              </div>
            </WdsStack>
          </template>
        </WdsStack>
      </WdsStack>
    </template>
  </ButtonDropdown>
</template>

<style scoped lang="scss">
.column-dragging {
  opacity: 0.3;
  background-color: rgba(0, 0, 0, 0.05);
  border-radius: 4px;
}
</style>
